import React, { useState, useMemo, useEffect } from 'react'
import { makeStyles } from '@material-ui/core'
import { Checkbox, Typography, Button, Icon } from 'foundations-library/components'
import { useAccountSecurityContext } from 'providers'
import { LoaderDialogContent, ErrorDialogContent, Dialog } from 'functional-foundations'
import { getVerbalColors } from 'helpers'
import SessionSignOut from './../../dialogs/SessionSignOut'
import { getRemoteValue } from './../../utils'
import Device from './Device'

const jss = (theme) => {
    const { colors } = theme['foundations-library']
    const verbalColors = getVerbalColors(colors)

    return {
        root: {
            display: 'flex',
            paddingTop: '20px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            borderRadius: '20px'
        },
        container: {
            display: 'flex',
            width: '100%',
            maxWidth: '578px',
            padding: '0px 40px 20px 40px',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '50px'
        },
        header: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '20px',
            alignSelf: 'stretch'
        },
        alertBox: {
            width: '100%',
            display: 'flex',
            padding: '10px 0px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            borderRadius: '10px',
            background: verbalColors['primary-50']
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '10px',
            alignSelf: 'stretch'
        },
        authorizedDevices: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            alignSelf: 'stretch',
            borderRadius: '20px',
            overflow: 'hidden',
            border: `1px solid ${verbalColors['neutral-100']}`,
            '& .header': {
                display: 'flex',
                padding: '10px 15px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '10px',
                alignSelf: 'stretch',
                borderRadius: '20px 20px 0px 0px',
                background: verbalColors['neutral-50'],
                boxShadow: '0px -1px 0px 0px rgba(133, 146, 154, 0.20) inset',
                '& [data-testid$=sign-out-check-label]': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    alignSelf: 'stretch',
                    '& [data-testid=sign-out-check]': {
                        display: 'none'
                    }
                }
            }
        },
        footer: {
            display: 'flex',
            paddingBottom: '30px',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: '20px',
            alignSelf: 'stretch',
            borderRadius: '0px 0px 20px 20px'
        },
        dialog: {
            '& .dialog-container': {
                padding: 0
            }
        }
    }
}

const useStyles = makeStyles(jss)

const texts = getRemoteValue('active_sessions')

const Wrapper = ({
    children,
    openInDialog,
    sessionSignOutDialogOpen
}: { children: JSX.Element, openInDialog: boolean, sessionSignOutDialogOpen: boolean }) => {
    const classes = useStyles()

    if (openInDialog && sessionSignOutDialogOpen) return <></> // if the session sign out dialog is open, don't show the authorized devices dialog
    return openInDialog ? <Dialog isOpen={true} className={classes.dialog} data-testid='session-management-dialog'>{children}</Dialog> : children
}

type PropsType = {
    hasPasswordUpdated?: boolean,
    logout?: () => any,
    onRedirectToLogin?: () => void
}

/**
 *
 */
const SessionManagement = ({
    hasPasswordUpdated = false,
    logout,
    onRedirectToLogin
}: PropsType): JSX.Element => {
    const classes = useStyles()
    const [checkedDevices, setCheckedDevices] = useState<Set<number>>(new Set([]))
    const [isSessionSignOutDialogOpen, setIsSessionSignOutDialogOpen] = useState(false)
    const { devices, setDevices, clearSessions, clearDeviceSessions } = useAccountSecurityContext()
    const deviceIds = useMemo(() => devices.data.map(device => device.id), [JSON.stringify(devices.data)])
    const selectedDevices = useMemo(() => devices.data.filter(device => checkedDevices.has(device.id)), [checkedDevices, JSON.stringify(devices.data)])
    const isCurrentDeviceIncluded = useMemo(() => selectedDevices.some(device => device.currentDevice), [selectedDevices])

    useEffect(() => {
        (async () => {
            const fetchedDevices = await setDevices() || []
            if (hasPasswordUpdated && fetchedDevices.length) {
                const currentDevice = fetchedDevices.find(device => device.currentDevice)
                if (currentDevice) setCheckedDevices(new Set([currentDevice.id]))
            }
        })()
    }, [])

    const togglecheckAll = (isChecked: boolean) => setCheckedDevices(new Set(isChecked ? deviceIds : []))

    const areAllDevicesChecked = () => checkedDevices.size > 0 && checkedDevices.size === deviceIds.length

    const renderHeader = () => {
        return (
            <div className='header'>
                <Checkbox
                    checked={areAllDevicesChecked()}
                    onChange={togglecheckAll}
                    data-testid='sign-out-check'
                >
                    <Typography variant='body2' color='neutral-700'>{texts.signOutCheck}</Typography>
                </Checkbox>
            </div>
        )
    }

    const renderDevices = () => {
        return devices.data.map((device) => {
            return <Device
                key={device.id}
                device={device}
                showCheckbox={true}
                shouldCurrentDeviceBeChecked={hasPasswordUpdated}
                isChecked={checkedDevices.has(device.id)}
                handleCheck={(id: number, isChecked: boolean) => {
                    setCheckedDevices(checkedDevices => {
                        const newCheckedDevices = new Set(checkedDevices)
                        isChecked ? newCheckedDevices.add(id) : newCheckedDevices.delete(id)
                        return newCheckedDevices
                    })
                }}
            />
        })
    }

    const renderFooter = () => {
        const handleSignOut = async () => {
            if (hasPasswordUpdated) {
                const data = await clearDeviceSessions(isCurrentDeviceIncluded, logout)
                if (data) setIsSessionSignOutDialogOpen(true)
            } else setIsSessionSignOutDialogOpen(true)
        }
        return (
            <div className={classes.footer}>
                <Button size='large' onClick={handleSignOut} disabled={!selectedDevices.length}>{texts.signOutBtn}</Button>
            </div>
        )
    }

    const reloadDevices = async () => {
        await setDevices(true)
        setCheckedDevices(new Set([]))
    }

    if (hasPasswordUpdated) {
        if (clearSessions.isLoading) return <Wrapper openInDialog={hasPasswordUpdated} sessionSignOutDialogOpen={isSessionSignOutDialogOpen}><LoaderDialogContent title='Clearing device sessions' subtitle='Please wait!' /></Wrapper>
        if (clearSessions.error) return <Wrapper openInDialog={hasPasswordUpdated} sessionSignOutDialogOpen={isSessionSignOutDialogOpen}><ErrorDialogContent title='Error' subtitle={clearSessions.error} /></Wrapper>
    }

    return (
        <>
            <Wrapper openInDialog={hasPasswordUpdated} sessionSignOutDialogOpen={isSessionSignOutDialogOpen}>
                <div className={classes.root}>
                    {
                        devices.isLoading
                            ? <LoaderDialogContent title='Loading active sessions' subtitle='Please wait!' />
                            : devices.error
                                ? <ErrorDialogContent title='Error' subtitle={devices.error} />
                                : <>
                                    <div className={classes.container}>
                                        <div className={classes.header}>
                                            {hasPasswordUpdated && <div className={classes.alertBox}>
                                                <Icon name='check-alt' color='primary-400' size={24} />
                                                <Typography variant='h7' color='neutral-700'>{texts.alert}</Typography>
                                            </div>}
                                            <div className={classes.content}>
                                                <Typography variant='subtitle1'>
                                                    {texts.title}
                                                </Typography>
                                                <div className={classes.authorizedDevices}>
                                                    {renderHeader()}
                                                    {renderDevices()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {renderFooter()}
                                </>
                    }
                </div>
            </Wrapper>
            <SessionSignOut
                isOpen={isSessionSignOutDialogOpen}
                setIsOpen={setIsSessionSignOutDialogOpen}
                selectedDevices={selectedDevices}
                reloadDevices={reloadDevices}
                isCurrentDeviceIncluded={isCurrentDeviceIncluded}
                logout={logout}
                onRedirectToLogin={onRedirectToLogin}
            />
        </>
    )
}

SessionManagement.displayName = 'SessionManagement'

export default SessionManagement
